/**
 * DAT error messages (A7 / ATL-001426). The API answers DAT failures with a
 * code (+ details); this turns them into translated, user-facing text:
 *
 *   const datError = useDatErrorMessage();
 *   showToast(datError(err, 'datLoadboard.users.toast.linkFailed'), 'error');
 *   datError.fromCode(result.errorCode, result.message)   // e.g. DAT Users verify results
 *
 * Codes and their meaning: packages/server/src/core/integrations/dat/dat-errors.ts.
 */
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { apiErrorMessage } from '../../hooks/use-forgot-password';
import { useDateTimeFormatters } from '../../../lib/datetime-format';

interface ApiErrorBody {
  code?: unknown;
  message?: unknown;
  details?: Record<string, unknown>;
}

function apiError(err: unknown): ApiErrorBody {
  return (err as { response?: { data?: { error?: ApiErrorBody } } })?.response?.data?.error ?? {};
}

export interface DatErrorMessage {
  (err: unknown, fallbackKey: string): string;
  /** For results that carry a code + server message instead of an HTTP error. */
  fromCode: (code: string | null | undefined, message?: string | null, details?: Record<string, unknown>) => string;
}

export function useDatErrorMessage(): DatErrorMessage {
  const { t } = useTranslation();
  const { formatDateTime } = useDateTimeFormatters();

  const fromCode = useCallback(
    (code: string | null | undefined, message?: string | null, details: Record<string, unknown> = {}): string => {
      const fallback = message ?? t('datLoadboard.errors.DAT_UNAVAILABLE');
      if (!code || !code.startsWith('DAT_')) return fallback;
      const at = (typeof details.retryAt === 'string' && details.retryAt) || (typeof details.resetsAt === 'string' && details.resetsAt) || null;
      const values = {
        detail: typeof details.detail === 'string' ? details.detail : '',
        time: at ? formatDateTime(at) : '',
        defaultValue: fallback,
      };
      // Time-aware variants when the API told us when to retry.
      if (code === 'DAT_UNAVAILABLE' && details.reason === 'circuit_open' && at) return t('datLoadboard.errors.DAT_UNAVAILABLE_PAUSED', values);
      if ((code === 'DAT_QUOTA_EXCEEDED' || code === 'DAT_RATE_LIMITED' || code === 'DAT_REFRESH_TOO_SOON') && at) return t(`datLoadboard.errors.${code}_UNTIL`, values);
      if (code === 'DAT_VALIDATION' && !values.detail) return t('datLoadboard.errors.DAT_VALIDATION_GENERIC', values);
      return t(`datLoadboard.errors.${code}`, values);
    },
    [t, formatDateTime],
  );

  return useMemo(() => {
    const fn = ((err: unknown, fallbackKey: string) => {
      const body = apiError(err);
      const code = typeof body.code === 'string' ? body.code : null;
      const fallback = apiErrorMessage(err, t(fallbackKey));
      return code ? fromCode(code, fallback, body.details ?? {}) : fallback;
    }) as DatErrorMessage;
    fn.fromCode = fromCode;
    return fn;
  }, [fromCode, t]);
}
